import { useState } from "react";
import { RefreshCcw, Search, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { RouterCombobox } from "./RouterCombobox";
import { SisaVoucher } from "./SisaVoucher";
import { VoucherRiwayat } from "./VoucherRiwayat";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STOP = new Set(["tolong", "minta", "pocer", "voucher", "kode", "yang", "diatas", "atas", "cek", "login", "bisa", "tidak", "komplen", "komplain", "gabisa", "masuk", "pagi", "siang", "malam", "sore", "mohon", "bantu", "saldo", "transfer", "sudah", "belum"]);
export const guessCodes = (messages) =>
  [...new Set(messages.slice(-30).reverse().flatMap((m) => (m.text || "").split(/[^A-Za-z0-9]+/))
    .filter((w) => /^[A-Za-z0-9]{4,8}$/.test(w) && !STOP.has(w.toLowerCase()) && !/^\d{1,3}$/.test(w)))].slice(0, 5);

export function CekKodeDialog({ open, onClose, channel, initialCode = "", suggestions = [] }) {
  const { routers } = useRouters();
  const [code, setCode] = useState(initialCode);
  const [routerId, setRouterId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [acting, setActing] = useState("");
  const [sisa, setSisa] = useState({ val: "", unit: "d", gb: "" });

  const sisaValidity = sisa.val && Number(sisa.val) > 0 ? `${Number(sisa.val)}${sisa.unit}` : "";
  const sisaGb = Number(sisa.gb) > 0 ? Number(sisa.gb) : 0;

  const cek = async (c = code) => {
    if (!c.trim()) return;
    setLoading(true); setData(null);
    try {
      const { data } = await api.post("/vouchers/cek-kode", { code: c.trim(), channel, router_id: routerId });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  const act = async (r, kind) => {
    const label = kind === "reset" ? "Reset (lepas MAC & putus sesi)" : "Ganti — buat ulang kode yang sama";
    const sisaTxt = kind === "ganti" && (sisaValidity || sisaGb) ? ` Sisa: ${[sisaValidity && `${sisa.val} ${sisa.unit === "d" ? "hari" : "jam"}`, sisaGb && `${sisaGb} GB`].filter(Boolean).join(", ")}.` : "";
    if (!window.confirm(`${label} voucher ${r.name} di ${r.router_name}?${sisaTxt}`)) return;
    setActing(`${r.router_id}-${kind}`);
    try {
      if (kind === "reset") await api.post(`/routers/${r.router_id}/vouchers/${encodeURIComponent(r.name)}/lepas-mac`, null, { params: { channel } });
      else await api.post(`/routers/${r.router_id}/vouchers/${encodeURIComponent(r.name)}/buat-ulang`, { channel, profile: r.profile || "", sisa_validity: sisaValidity, sisa_gb: sisaGb });
      toast.success(kind === "reset" ? `Voucher ${r.name} direset` : `Voucher ${r.name} dibuat ulang`);
      cek(r.name);
    } catch (e) { toast.error(errMsg(e)); } finally { setActing(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dialog-cek-kode">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Search className="w-5 h-5 text-[#F04E23]" /> Cek Kode Voucher</DialogTitle></DialogHeader>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); cek(); }}>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Kode voucher dari chat agen" className="font-mono" autoFocus data-testid="cek-kode-input" />
          <Button type="submit" disabled={loading || !code.trim()} data-testid="cek-kode-submit">{loading ? "Mengecek..." : "Cek"}</Button>
        </form>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground shrink-0">Cabang / Router:</span>
          <RouterCombobox routers={[...routers].sort((a, b) => a.name.localeCompare(b.name))} value={routerId || "ALL"} onChange={(v) => setRouterId(v === "ALL" ? "" : v)} allLabel="Otomatis (cari di semua router)" testid="cek-kode-router" />
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 text-xs" data-testid="cek-kode-suggestions">
            <span className="text-muted-foreground">Terdeteksi di chat:</span>
            {suggestions.map((s) => <button key={s} type="button" className="font-mono px-2 py-0.5 rounded border border-border hover:border-[#F04E23] hover:text-[#F04E23] transition-colors" onClick={() => { setCode(s); cek(s); }} data-testid={`cek-kode-sug-${s}`}>{s}</button>)}
          </div>
        )}
        {data?.code && <VoucherRiwayat code={data.code} routerId={routerId} />}
        {data?.results?.map((r) => (
          <div key={r.router_id} className="rounded-xl border border-border p-3 space-y-2 text-sm" data-testid={`cek-kode-result-${r.router_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold">{r.router_name}</div>
              <div className="font-mono text-xs text-muted-foreground">{r.profile || "-"}</div>
            </div>
            {r.error ? <div className="text-rose-400 text-xs">Router tidak merespons: {r.error}</div> : (
              <>
                {r.findings.map((f, i) => (
                  <div key={i} className={`rounded-lg border px-3 py-2 text-xs ${f.level === "error" ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : f.level === "ok" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-200"}`}>{f.text}</div>
                ))}
                {r.found && (
                  <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs rounded-lg border border-border px-3 py-2" data-testid={`cek-kode-info-${r.router_id}`}>
                    <span className="text-muted-foreground">Tanggal Login</span><span className="font-mono text-right">{r.used_at ? fmtDateTime(r.used_at) : "Belum pernah login"}</span>
                    {r.sisa !== null && r.sisa !== undefined && <><span className="text-muted-foreground">Sisa</span><span className="font-mono text-right"><SisaVoucher s={r.sisa} disabled={r.disabled} /></span></>}
                  </div>
                )}
                {r.db && <div className="text-[11px] text-muted-foreground font-mono">DB: {r.db.status || "-"} • batch {r.db.batch_id || "-"} • dibuat {r.db.created_by || "-"}</div>}
                <div className="rounded-lg border border-dashed border-border px-3 py-2 space-y-1.5" data-testid={`cek-kode-sisa-${r.router_id}`}>
                  <div className="text-[11px] text-muted-foreground">Sisa masa berlaku saat <b>Ganti</b> (opsional, mulai dihitung sekarang):</div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <Input type="number" min="0" className="h-7 w-20 font-mono" placeholder="18" value={sisa.val} onChange={(e) => setSisa({ ...sisa, val: e.target.value })} data-testid="cek-kode-sisa-val" />
                    <select className="h-7 rounded-md border border-input bg-background px-2 text-xs" value={sisa.unit} onChange={(e) => setSisa({ ...sisa, unit: e.target.value })} data-testid="cek-kode-sisa-unit">
                      <option value="d">hari</option><option value="h">jam</option>
                    </select>
                    <span className="text-muted-foreground">•</span>
                    <Input type="number" min="0" step="0.1" className="h-7 w-20 font-mono" placeholder="GB" value={sisa.gb} onChange={(e) => setSisa({ ...sisa, gb: e.target.value })} data-testid="cek-kode-sisa-gb" />
                    <span className="text-muted-foreground">GB</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  {r.found && (
                    <Button size="sm" variant="outline" className="flex-1" disabled={!!acting} onClick={() => act(r, "reset")} data-testid={`cek-kode-reset-${r.router_id}`}>
                      <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />{acting === `${r.router_id}-reset` ? "Mereset..." : "Reset"}
                    </Button>
                  )}
                  <Button size="sm" className={`flex-1 ${r.actions.includes("ganti") ? "bg-[#F04E23] hover:bg-[#d84420]" : ""}`} variant={r.actions.includes("ganti") ? "default" : "secondary"} disabled={!!acting} onClick={() => act(r, "ganti")} data-testid={`cek-kode-ganti-${r.router_id}`}>
                    <Wand2 className="w-3.5 h-3.5 mr-1.5" />{acting === `${r.router_id}-ganti` ? "Membuat..." : "Ganti (kode sama)"}
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
